import SectionPanel from '@/components/SectionPanel';
import { BiBuilding, BiEnvelope, BiUser, BiWallet } from 'react-icons/bi';
import { SimpleGrid } from "@chakra-ui/react"
import CurrencySelectController from '@/components/fields/CurrencySelect';
import ErrorMessage from "src/components/ErrorMessage";
import CountrySelectController from '@/components/fields/CountrySelect';
import { useForm } from "react-hook-form";
import { useCallback, useEffect, useState, JSX, ReactNode } from 'react';
import { Partner } from 'src/api/Partner';
import { partnerService } from 'src/service/PartnerService';
import { useNavigate } from 'react-router-dom';
import CancelButton from 'src/components/buttons/CancelButton';
import SubmitButton from '@/components/buttons/SubmitButton';
import { FormInput } from '@/components/fields/FormInput';
import { toaster } from '@/components/ui/toaster';
import FormPage from '@/components/FormPage';
import { useI18n } from '@/i18n/I18nContext';

export interface PartnersFormProps {
    partnerId?: number;
    organizationId: number;
    onSubmit: (partner: Partner) => Promise<void>;
    viewOnly?: boolean;
    header: string;
    // set the flag when want to explicitly edit consultan or partner
    // if not set then will be configured at loading time in the effect hook
    // e.g. if partnerId is set, then this flag will be derived at loading time
    consultantOnly?: boolean;
    footerActions?: ReactNode;
}

const PartnersForm = ({ partnerId, organizationId, onSubmit, viewOnly, header, consultantOnly, footerActions }: PartnersFormProps): JSX.Element => {
    const { t } = useI18n();
    const { control, register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<Partner>({
        defaultValues: {
            id: partnerId,
            organizationId: organizationId,
            createdAt: new Date(),
            consultant: consultantOnly,
        }
    });
    const [serverError, setServerError] = useState('');
    const [consultant, setConsultant] = useState<boolean | null>(consultantOnly ?? null);
    const navigate = useNavigate();

    const onSubmitInternal = useCallback(
        (p: Partner): Promise<void> => {
            return onSubmit(p).then(() => {
                toaster.create({
                    title: p.id && p.id > -1 ? 'Partner aktualisiert' : 'Partner hinzugefügt',
                    type: 'success'
                });
                navigate(p.consultant ? '/partners/consultants' : '/partners');
            }).catch(err => setServerError('Fehler'));
        }, [onSubmit, navigate]
    );

    useEffect(() => {
        if (partnerId > -1) {
            partnerService.find(partnerId).then(p => {
                setConsultant(p.consultant);
                console.log(`consultant=${p.consultant}`)
                reset(p)
            });
        }
    }, [partnerId, reset]);
    const readOnly = viewOnly ?? false;
    // console.info(`form is readonly: ${readOnly}`);

    return (
        <FormPage title={`${header} ${consultant ? 'Konsulent' : 'Partner'}`} description={t('forms.partners.description')}
            onSubmit={handleSubmit(onSubmitInternal)} invalid={!!serverError}
            actions={<>
                {consultant === false && footerActions}
                <CancelButton onClick={() => navigate(consultant ? '/partners/consultants/' : '/partners/')} />
                {!readOnly && <SubmitButton isSubmitting={isSubmitting} />}
            </>}>
            {serverError && <ErrorMessage message={serverError} />}
            <SectionPanel title={t('partners.details.information')}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <FormInput name="name" label='Name' register={register} error={errors.name} required disabled={readOnly} icon={BiUser} autoFocus={true} />

                    <CountrySelectController control={control} name='country' readOnly={readOnly} />
                </SimpleGrid>
                <FormInput name="email" label='E-Mail' register={register} error={errors.email} required disabled={readOnly} icon={BiEnvelope} />
            </SectionPanel>
            <SectionPanel title={t('partners.details.bankDetails')}>
                <FormInput name="bankName" label='Bank' register={register} error={errors.bankName} required disabled={readOnly} icon={BiBuilding} placeholder='Bankverbindung' />

                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <FormInput name="accountName" label='Konto-Inhaber' register={register} error={errors.accountName} required disabled={readOnly} icon={BiUser} />

                    <CurrencySelectController control={control} name='currency' readOnly={readOnly} />
                </SimpleGrid>


                <SimpleGrid columns={{ base: 1, md: 2 }} gap='4'>
                    <FormInput name="accountNumber" label='Konto Nr' register={register} error={errors.accountNumber} required disabled={readOnly} icon={BiUser} />

                    <FormInput name="swiftCode" label='SWIFT Code' register={register} error={errors.swiftCode} required disabled={readOnly} icon={BiWallet} />
                </SimpleGrid>

                <FormInput name="bankCode" label='Bank Code' register={register} disabled={readOnly} />

            </SectionPanel>
        </FormPage>
    );
}

export default PartnersForm
